import React, { useEffect, Dispatch, SetStateAction, useState, useRef, Fragment } from 'react';
import classnames from 'classnames';
import cloneDeep from 'lodash.clonedeep';
import Erasure from 'sunzi-plugin-erasure';
import { Header } from '@/components';
import { i18n, loadHTMLImage } from '@/utils';
import { UserImage } from './mini-me';
import Icon from './icon';
import styles from './style.less';

interface EditProps {
  /** 用户上传头像 */
  images: UserImage[];
  /** 设置用户上传头像 */
  setImages: Dispatch<SetStateAction<UserImage[]>>;
  /** 设置 zuploader 组件显影 */
  setZuploaderVisible: Dispatch<SetStateAction<boolean>>;
  /** 设置裁剪显影 */
  setClipVisible: Dispatch<SetStateAction<boolean>>;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm: () => void;
  /** 谷歌统计 */
  gtag: any;
}

const Edit: React.FC<EditProps> = ({
  images,
  setImages,
  setZuploaderVisible,
  setClipVisible,
  onClose,
  onConfirm,
  gtag
}) => {

  const _canvas = useRef<HTMLCanvasElement>(null);
  /** 橡皮檫组件实例 */
  const _erasure = useRef<Erasure>();
  /** 橡皮檫显隐 */
  const [ erasureVisible, setErasureVisible ] = useState<boolean>(false);
  /** 橡皮檫撤回状态 */
  const [ revokeDisabled, setRevokeDisabled ] = useState<boolean>(true);

  useEffect(() => {
    const canvas = _canvas.current;
    if (canvas) {
      const { offsetWidth, offsetHeight } = canvas;
      const scale = Math.min(1000 / offsetWidth, 1000 / offsetHeight);
      canvas.width = offsetWidth * scale;
      canvas.height = offsetHeight * scale;
      loadHTMLImage(images[images.length - 1].image!).then(image => {
        _erasure.current = new Erasure(canvas, image, {
          padding: 60,
          color: 'rgba(255, 0, 0, 0.5)',
          onStackChange: (stack) => {
            if (stack.length === 0 && !revokeDisabled)
              setRevokeDisabled(true);
            else if (stack.length > 0 && revokeDisabled)
              setRevokeDisabled(false);
          }
        });
        _erasure.current.modeToggle();
      });
    }
  }, []);

  /** 橡皮擦显隐 */
  const handleErasureVisible = (visible: boolean) => {
    setErasureVisible(visible);
    _erasure.current?.modeToggle();
    if (visible) gtag('event', 'event_erasure_open');
    else gtag('event', 'event_erasure_close');
  }

  /** 橡皮檫关闭 */
  const handleErasureClosed = () => {
    _erasure.current?.reset();
    handleErasureVisible(false);
  }

  /** 橡皮檫确认 */
  const handleErasureConfirm = async () => {
    const result = await _erasure.current?.toErasureURL();
    if (result) {
      const htmlImage = await loadHTMLImage(result);
      images[images.length - 1].image = result;
      images[images.length - 1].height = htmlImage.height;
      images[images.length - 1].width = htmlImage.width;
      setImages(cloneDeep(images));
      onClose();
      _erasure.current?.dispose();
      gtag('event', 'event_erasure_confirm');
    }
  }

  return (
    <div className={styles.edit}>
      <Header
        className={classnames(styles.editHeader, {
          [styles.editHeaderHide]: erasureVisible
        })}
        closeIconType='left'
        closeIconColor='#000'
        onClose={onClose}
        renderContent={
          <span className={styles.editHeaderContent}>
            {i18n.format('modules.mini.me.edit.title')}
          </span>
        }
        renderSubmit={
          <div
            className={styles.editHeaderSubmit}
            onClick={onConfirm}
          >
            {i18n.format('modules.global.done')}
          </div>
        }
      />
      <div className={styles.editContent}>
        <canvas ref={_canvas} />
      </div>
      <div
        className={classnames(styles.erasureButtonGroup, {
          [styles.erasureButtonGroupDown]: erasureVisible
        })}
      >
        {erasureVisible ?
          <Fragment>
            <div className={styles.buttonGroup}>
              <div
                className={styles.erasureClose}
                onClick={handleErasureClosed}
              >
                <Icon type="close" size={30} color="#fff" />
              </div>
              {!revokeDisabled &&
              <div
                className={styles.erasureRevoke}
                onClick={() => _erasure.current?.revoke()}
              >
                <Icon type="revoke" size={26} />
              </div>
              }
            </div>
            <div
              className={styles.erasureConfirm}
              onClick={handleErasureConfirm}
            >
              <Icon type="confirm" size={30} color="#fff" />
            </div>
          </Fragment> :
          <div
            className={styles.erasureButton}
            onClick={() => handleErasureVisible(true)}
          >
            <Icon type="erasure" size={30} color="#000" />
          </div>
        }
      </div>
      <div className={classnames(styles.footer, {
        [styles.footerHide]: erasureVisible
      })}>
        <div
          className={styles.button}
          onClick={() => setZuploaderVisible(true)}
        >
          <Icon type='photo' size={30} />
          <span>{i18n.format('modules.global.replace')}</span>
        </div>
        <div className={styles.button} onClick={() => setClipVisible(true)}>
          <Icon type='scissors' size={30} />
          <span>{i18n.format('modules.global.cropper')}</span>
        </div>
      </div>
    </div>
  )
}

export default Edit;
